/**
 * Cálculos de contraste para la personalización de colores por cliente.
 *
 * Sin dependencias de Angular a propósito, para poder probarlos de forma aislada. Los colores
 * llegan siempre como #RRGGBB (el backend y el servicio ya los validaron).
 */

/** Superficies reales de la tarjeta del login, contra las que se mide el contraste. */
export const SUPERFICIE_CLARA = '#FFFFFF';
export const SUPERFICIE_OSCURA = '#09090B';

/** Contraste mínimo para elementos de interfaz (WCAG 1.4.11: 3:1). */
export const CONTRASTE_MINIMO_UI = 3;

/** Luminancia relativa WCAG de un color #RRGGBB. */
export function luminancia(colorHex: string): number {
  const canal = (inicio: number): number => {
    const valor = parseInt(colorHex.slice(inicio, inicio + 2), 16) / 255;
    return valor <= 0.03928 ? valor / 12.92 : Math.pow((valor + 0.055) / 1.055, 2.4);
  };

  return 0.2126 * canal(1) + 0.7152 * canal(3) + 0.0722 * canal(5);
}

/** Razón de contraste WCAG entre dos luminancias (de 1 a 21). */
export function razonDeContraste(luminanciaA: number, luminanciaB: number): number {
  const [mayor, menor] = luminanciaA >= luminanciaB ? [luminanciaA, luminanciaB] : [luminanciaB, luminanciaA];
  return (mayor + 0.05) / (menor + 0.05);
}

/** Mezcla dos colores #RRGGBB; `peso` es la parte de `destino` (0 = origen, 1 = destino). */
export function mezclar(origenHex: string, destinoHex: string, peso: number): string {
  const canal = (hex: string, inicio: number): number => parseInt(hex.slice(inicio, inicio + 2), 16);
  const mezcla = (inicio: number): string =>
    Math.round(canal(origenHex, inicio) * (1 - peso) + canal(destinoHex, inicio) * peso)
      .toString(16)
      .padStart(2, '0');

  return `#${mezcla(1)}${mezcla(3)}${mezcla(5)}`.toUpperCase();
}

/** Blanco u oscuro, el que mejor se lea escrito sobre `colorHex`. */
export function textoLegibleSobre(colorHex: string): string {
  return luminancia(colorHex) > 0.179 ? '#111827' : '#FFFFFF';
}

/**
 * Devuelve el color del cliente tal cual si ya contrasta lo suficiente con la superficie; si no,
 * lo oscurece (sobre superficie clara) o lo aclara (sobre oscura) lo MÍNIMO necesario. Así un
 * amarillo sigue siendo amarillo-ámbar y no se vuelve negro, pero un blanco sobre blanco deja de
 * ser invisible.
 */
export function ajustarParaSuperficie(
  colorHex: string,
  superficieHex: string,
  minimo: number = CONTRASTE_MINIMO_UI
): string {
  const luminanciaSuperficie = luminancia(superficieHex);
  const extremo = luminanciaSuperficie > 0.5 ? '#000000' : '#FFFFFF';

  for (let paso = 0; paso <= 40; paso++) {
    const candidato = paso === 0 ? colorHex : mezclar(colorHex, extremo, paso / 40);
    if (razonDeContraste(luminancia(candidato), luminanciaSuperficie) >= minimo) {
      return candidato;
    }
  }

  return extremo;
}
